/**
 * Generic utility for persisting query parameters across page navigation
 *
 * Automatically saves URL parameters to localStorage and restores them when returning
 * to the page without parameters. Enables users to pick up where they left off.
 *
 * @example
 * // Initialize for hotspots page
 * const persistence = initQueryParamPersistence('hotspots', [
 *   'driver_id', 'attribute_ids', 'min_responses', 'squad_ids'
 * ]);
 *
 * // Wait for restoration to complete before reading URL
 * persistence.whenReady(() => {
 *   // URL params are now available, safe to initialize UI
 *   initializeFromUrl();
 * });
 *
 * // Save params when updating URL
 * function updateUrl() {
 *   window.history.replaceState({}, "", newUrl);
 *   persistence.saveParams(); // Save to localStorage
 * }
 *
 * // Check if params were restored (useful for cache invalidation)
 * if (persistence.wasRestoredFromStorage()) {
 *   // Handle restored state differently than fresh page load
 * }
 */

const STORAGE_PREFIX = "params";

/**
 * Initialize query parameter persistence for a specific page
 * @param {string} pageKey - Unique key for this page/feature (e.g., 'hotspots')
 * @param {string[]} paramNames - Array of parameter names to track
 * @returns {object} API for interacting with the persistence system
 */
export function initQueryParamPersistence(pageKey, paramNames) {
  // Create a unique storage key like 'params_snapshots_<snapshot_id>_hotspots_<pageKey>'
  const storageKey = `${STORAGE_PREFIX}${window.location.pathname.replaceAll("/", "_")}_${pageKey}`;

  // On page load, restore params from localStorage if URL has no params
  const wasRestored = restoreParamsFromStorage(storageKey, paramNames);

  // Watch for URL changes and save params to localStorage
  const saveParams = watchUrlChanges(storageKey, paramNames);

  // Return API for the page to use
  return {
    getCurrentParams: () => getCurrentParams(paramNames),
    wasRestoredFromStorage: () => wasRestored,
    saveParams: saveParams,
    whenReady: (callback) => {
      // Since restoration is synchronous, we can call immediately
      // But wrap in requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => callback());
    },
  };
}

/**
 * Restore parameters from localStorage to URL if URL has no relevant params
 * @returns {boolean} Whether params were restored from localStorage
 */
function restoreParamsFromStorage(storageKey, paramNames) {
  const currentParams = new URLSearchParams(window.location.search);

  // Check if URL already has any of the tracked params
  const hasParams = paramNames.some((param) => currentParams.has(param));

  if (!hasParams) {
    // No params in URL, try to restore from localStorage
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const storedParams = JSON.parse(stored);

        // Add stored params to current URL
        const url = new URL(window.location);
        let paramsAdded = false;

        paramNames.forEach((param) => {
          if (storedParams[param]) {
            url.searchParams.set(param, storedParams[param]);
            paramsAdded = true;
          }
        });

        if (paramsAdded) {
          // Update URL without triggering page reload
          window.history.replaceState({}, "", url);
          return true;
        }
      }
    } catch (error) {
      console.warn("Failed to restore query params from localStorage:", error);
    }
  }

  return false;
}

/**
 * Set up minimal URL change watching for back/forward buttons only
 */
function watchUrlChanges(storageKey, paramNames) {
  function saveCurrentParams() {
    const currentParams = new URLSearchParams(window.location.search);
    const paramsToSave = {};
    let hasParams = false;

    paramNames.forEach((param) => {
      const value = currentParams.get(param);
      if (value) {
        paramsToSave[param] = value;
        hasParams = true;
      }
    });

    if (hasParams) {
      // Save to localStorage
      try {
        localStorage.setItem(storageKey, JSON.stringify(paramsToSave));
      } catch (error) {
        console.warn("Failed to save query params to localStorage:", error);
      }
    }
  }

  // Save params initially if we have any
  const currentParams = new URLSearchParams(window.location.search);
  const hasRelevantParams = paramNames.some((param) =>
    currentParams.has(param),
  );

  if (hasRelevantParams) {
    saveCurrentParams();
  }

  // Only listen for popstate events (back/forward buttons)
  window.addEventListener("popstate", saveCurrentParams);

  // Cleanup on page unload
  function cleanup() {
    window.removeEventListener("popstate", saveCurrentParams);
  }

  window.addEventListener("beforeunload", cleanup);

  // Return the save function for explicit use
  return saveCurrentParams;
}

/**
 * Get current values for tracked parameters from URL
 */
function getCurrentParams(paramNames) {
  const currentParams = new URLSearchParams(window.location.search);
  const params = {};

  paramNames.forEach((param) => {
    const value = currentParams.get(param);
    if (value) {
      params[param] = value;
    }
  });

  return params;
}
